// src/pages/Campaigns/VolunteerManagement/OutreachSmsWizard.js
import React, { useState, useEffect } from 'react';
import {
  Box, Stepper, Step, StepLabel, Button, Typography, Paper,
  TextField, Alert, CircularProgress, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Checkbox,
  TablePagination, Chip, IconButton, Grid, InputAdornment,
  Pagination, FormControlLabel, Tabs, Tab
} from '@mui/material';
import { Sms, CheckCircle, ArrowBack, Search as SearchIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const steps = ['Select Contacts', 'Connect SMS', 'Compose', 'Review & Send'];

const OutreachSmsWizard = () => {
  const { chatbotId, eventId } = useParams();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Data States
  const [event, setEvent] = useState(null);
  
  // Consented Contacts State
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]); // Subscription IDs
  
  // CRM State
  const [crmContacts, setCrmContacts] = useState([]);
  const [selectedCrmIds, setSelectedCrmIds] = useState([]);
  
  // Tab State
  const [sourceTab, setSourceTab] = useState(0); // 0 = Consented, 1 = CRM

  // SMS Configuration
  const [smsConfigured, setSmsConfigured] = useState(false);
  const [checkingConfig, setCheckingConfig] = useState(false);
  const [messageBody, setMessageBody] = useState('');
  
  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadEvent();
    loadConsentedContacts();
  }, []);

  useEffect(() => {
    if (activeStep === 1) checkSmsStatus();
  }, [activeStep]);

  // Trigger CRM load when tab is switched
  useEffect(() => {
      if (sourceTab === 1 && crmContacts.length === 0) {
          loadCrmContacts();
      }
  }, [sourceTab]);

  // Filter logic for Consented Contacts search
  useEffect(() => {
    if (activeStep === 0 && sourceTab === 0) {
        const lowerSearch = searchTerm.toLowerCase();
        const filtered = contacts.filter(c => 
            (c.phone && c.phone.includes(lowerSearch)) || 
            (c.email && c.email.toLowerCase().includes(lowerSearch))
        );
        setFilteredContacts(filtered);
        setPage(0);
    }
  }, [searchTerm, contacts, activeStep, sourceTab]);

  const loadEvent = async () => {
    try {
      const res = await axiosInstance.get('/api/events', { params: { chatbot_id: chatbotId } });
      const foundEvent = res.data.data.find(e => e.id === parseInt(eventId));
      setEvent(foundEvent);
      
      if (foundEvent) {
        setMessageBody(`Volunteer opportunity for ${foundEvent.name}. Please confirm your availability: {{availability_link}}`);
      }
    } catch (e) { console.error(e); }
  };

  const loadConsentedContacts = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/api/user/subscriptions');
      const validContacts = (res.data.data || []).filter(c => c.phone && c.is_active);
      setContacts(validContacts);
      setFilteredContacts(validContacts);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadCrmContacts = async () => {
    try {
      setLoading(true);
      // Fetch all for simplicty in this example, pagination ideally should be server side
      const res = await axiosInstance.get('/api/crm/contacts', { params: { chatbot_id: chatbotId, per_page: 1000 } });
      // Filter for valid phones
      const valid = (res.data.data || []).filter(c => c.phone || c.phone_mobile);
      setCrmContacts(valid);
    } catch (e) { console.error(e); } 
    finally { setLoading(false); }
  };

  const checkSmsStatus = async () => {
    setCheckingConfig(true);
    try {
      const res = await axiosInstance.get('/sms/service/status');
      setSmsConfigured(res.data.data?.is_configured || false);
    } catch (e) {
      console.error("SMS Status Check Failed:", e);
      setSmsConfigured(false);
    } finally {
      setCheckingConfig(false);
    }
  };

  const handleSend = async () => {
    try {
      setLoading(true);
      await axiosInstance.post('/api/outreach/campaign', {
        event_id: parseInt(eventId),
        subscription_ids: sourceTab === 0 ? selectedIds : [],
        crm_contact_ids: sourceTab === 1 ? selectedCrmIds : [], // Send CRM IDs
        use_consented_contacts: sourceTab === 0,
        send_email: false,
        send_sms: true,
        campaign_name: `SMS Campaign for ${event?.name}`,
        sms_message: messageBody,
      }, { params: { chatbot_id: chatbotId } });
      
      // Success handling...
      alert(`SMS Campaign Sent Successfully!`);
      navigate(`/chatbot/${chatbotId}/campaigns/outreach/${eventId}`);
    } catch (e) {
      const errorData = e.response?.data;
      alert(errorData?.message || 'Failed to send SMS');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
        if (sourceTab === 0 && selectedIds.length === 0) return alert('Select at least one consented contact');
        if (sourceTab === 1 && selectedCrmIds.length === 0) return alert('Select at least one CRM contact');
    }
    if (activeStep === 1 && !smsConfigured) {
      checkSmsStatus(); 
      if(!smsConfigured) return alert('Please configure an SMS service first');
    }
    if (activeStep === 2 && !messageBody.includes('{{availability_link}}')) return alert('Message must contain {{availability_link}}');
    
    setActiveStep((prev) => prev + 1);
  };

  // Toggle All for Consented
  const handleSelectAllConsented = (event) => {
    if (event.target.checked) {
      setSelectedIds(filteredContacts.map(c => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  // Toggle All for CRM
  const handleSelectAllCrm = (event) => {
    if (event.target.checked) {
        // CRM pagination is client-side here for simplicity of example, selecting all loaded
        setSelectedCrmIds(crmContacts.map(c => c.id));
    } else {
        setSelectedCrmIds([]);
    }
  };

  const handleRowClick = (id) => {
    const selectedIndex = selectedIds.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedIds, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedIds.slice(1));
    } else if (selectedIndex === selectedIds.length - 1) {
      newSelected = newSelected.concat(selectedIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selectedIds.slice(0, selectedIndex), selectedIds.slice(selectedIndex + 1));
    }
    setSelectedIds(newSelected);
  };

  // --- Render Steps ---

  const renderSelectStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Select Recipients</Typography>
      
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={sourceTab} onChange={(e, v) => { setSourceTab(v); setPage(0); }}>
              <Tab label="Consented Contacts" />
              <Tab label="CRM Contacts" />
          </Tabs>
      </Box>

      {/* Search (Only for Consented currently implemented in this simplified flow) */}
      {sourceTab === 0 && (
      <Box sx={{ mb: 2, maxWidth: 400 }}>
        <TextField
            fullWidth
            size="small"
            placeholder="Search phone number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
            }}
        />
      </Box>
      )}

      {loading ? <CircularProgress /> : (
        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox 
                        checked={
                            sourceTab === 0 
                            ? (filteredContacts.length > 0 && selectedIds.length === filteredContacts.length)
                            : (crmContacts.length > 0 && selectedCrmIds.length === crmContacts.length)
                        }
                        onChange={sourceTab === 0 ? handleSelectAllConsented : handleSelectAllCrm}
                    />
                  </TableCell>
                  <TableCell>{sourceTab === 0 ? 'Phone Number' : 'Name'}</TableCell>
                  <TableCell>{sourceTab === 0 ? 'Email' : 'Phone'}</TableCell>
                  <TableCell>{sourceTab === 0 ? 'Source' : 'Company'}</TableCell>
                  {sourceTab === 0 && <TableCell>Consented Date</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {sourceTab === 0 ? (
                    // Consented List
                    filteredContacts
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((row) => {
                            const isItemSelected = selectedIds.indexOf(row.id) !== -1;
                            return (
                            <TableRow 
                                key={row.id} 
                                hover 
                                onClick={() => handleRowClick(row.id)}
                                role="checkbox" 
                                aria-checked={isItemSelected} 
                                selected={isItemSelected} 
                                sx={{ cursor: 'pointer' }}
                            >
                                <TableCell padding="checkbox">
                                <Checkbox checked={isItemSelected} />
                                </TableCell>
                                <TableCell>{row.phone}</TableCell>
                                <TableCell>{row.email || '-'}</TableCell>
                                <TableCell>
                                    <Chip label={row.source || 'Unknown'} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>
                                    {row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'}
                                </TableCell>
                            </TableRow>
                            );
                    })
                ) : (
                    // CRM List
                    crmContacts
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((row) => {
                            const isItemSelected = selectedCrmIds.includes(row.id);
                            return (
                                <TableRow
                                    key={row.id}
                                    hover
                                    onClick={() => {
                                        const idx = selectedCrmIds.indexOf(row.id);
                                        let newSel = idx === -1 ? [...selectedCrmIds, row.id] : selectedCrmIds.filter(id => id !== row.id);
                                        setSelectedCrmIds(newSel);
                                    }}
                                    role="checkbox"
                                    aria-checked={isItemSelected}
                                    selected={isItemSelected}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell padding="checkbox"><Checkbox checked={isItemSelected} /></TableCell>
                                    <TableCell>{row.first_name} {row.last_name}</TableCell>
                                    <TableCell>{row.phone_mobile || row.phone}</TableCell>
                                    <TableCell>{row.account?.name || '-'}</TableCell>
                                </TableRow>
                            );
                        })
                )}
                
                {((sourceTab === 0 && filteredContacts.length === 0) || (sourceTab === 1 && crmContacts.length === 0)) && (
                  <TableRow><TableCell colSpan={5} align="center">No contacts found.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={sourceTab === 0 ? filteredContacts.length : crmContacts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
            }}
          />
        </Paper>
      )}
      <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Selected: {sourceTab === 0 ? selectedIds.length : selectedCrmIds.length} contact(s)
      </Typography>
    </Box>
  );

  const renderConnectStep = () => (
    <Box textAlign="center" py={5}>
      {checkingConfig ? <CircularProgress /> : (
        <>
          <Sms sx={{ fontSize: 60, color: smsConfigured ? 'success.main' : 'grey.400', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {smsConfigured ? 'SMS Service Connected' : 'Connect SMS Service'}
          </Typography>
          
          {smsConfigured ? (
             <Alert severity="success" sx={{ display: 'inline-flex', mt: 2 }} icon={<CheckCircle />}>
                Ready to send messages
             </Alert>
          ) : (
            <Box mt={2}>
              <Alert severity="warning" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                You have not configured an SMS provider (Twilio, AWS, etc.) yet.
              </Alert>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/Sms`)}
              >
                Go to SMS Configuration
              </Button>
              <Button onClick={checkSmsStatus} sx={{ ml: 2 }}>
                Check Again
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );

  const renderComposeStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Compose SMS Message</Typography>
      <TextField
        fullWidth
        multiline
        rows={4}
        label="SMS Content"
        value={messageBody}
        onChange={(e) => setMessageBody(e.target.value)}
        helperText={`${messageBody.length} characters. Ensure {{availability_link}} is included.`}
        error={!messageBody.includes('{{availability_link}}')}
      />
      <Alert severity="info" sx={{ mt: 2 }}>
        Variable <strong>{`{{availability_link}}`}</strong> will be replaced with the unique availability confirmation link for each volunteer.
      </Alert>
    </Box>
  );

  const renderReviewStep = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        You are about to send SMS to <strong>{sourceTab === 0 ? selectedIds.length : selectedCrmIds.length}</strong> contacts for <strong>{event?.name}</strong>.
      </Alert>
      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="caption" color="text.secondary">Preview:</Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>{messageBody}</Typography>
      </Paper>
      <Alert severity="success" sx={{ mt: 2 }}>
        A unique availability confirmation link will be added to each SMS automatically.
      </Alert>
    </Box>
  );

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={4}>
        <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/outreach/${eventId}`)} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold">
          Send Volunteer SMS Invitations
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
        {steps.map((label) => (<Step key={label}><StepLabel>{label}</StepLabel></Step>))}
      </Stepper>

      <Paper sx={{ p: 3, mb: 3, minHeight: 300 }}>
        {activeStep === 0 && renderSelectStep()}
        {activeStep === 1 && renderConnectStep()}
        {activeStep === 2 && renderComposeStep()}
        {activeStep === 3 && renderReviewStep()}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button onClick={() => activeStep === 0 ? navigate(`/chatbot/${chatbotId}/campaigns/outreach/${eventId}`) : setActiveStep(prev => prev - 1)}>
          Back
        </Button>
        {activeStep === steps.length - 1 ? (
          <Button variant="contained" onClick={handleSend} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Send SMS'}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleNext} disabled={activeStep === 1 && !smsConfigured}>
            Next
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default OutreachSmsWizard;